/**
 * useProjectAggregation Hook
 * Extracted from MultiProjectContext.tsx
 * Handles aggregation of nodes and data from multiple visible projects
 */

import { useMemo } from 'react';
import { ProjectData, ProjectNode, KanbanColumnConfig } from '@/shared/types/projectData';
import { log } from '@/shared/utils/logger';
import { getNodeId, getProjectIdFromNodeId } from '../utils/projectUtils';
import { getProjectCompanyLabel } from '../utils/projectCompany';
import type { ProjectDataWithMeta, AggregatedNode } from '../types/multiProject';

interface UseProjectAggregationParams {
  visibleProjectIds: Set<string>;
  projectsData: Map<string, ProjectDataWithMeta>;
  activeProjectId: string | null;
}

export function useProjectAggregation({
  visibleProjectIds,
  projectsData,
  activeProjectId
}: UseProjectAggregationParams) {

  // Aggregate nodes from VISIBLE projects (with namespacing for display only)
  const aggregatedNodes = useMemo(() => {
    const nodes: AggregatedNode[] = [];
    const visibleIds = Array.from(visibleProjectIds);
    const isSingleProjectView = visibleIds.length === 1;

    // NUCLEAR OPTION: Skip aggregation and namespacing for single project
    visibleIds.forEach(projectId => {
      // Skip invalid project IDs
      if (!projectId || projectId.includes(',')) {
        log.error('MultiProject', '🚨 [MULTI-PROJECT DEBUG] Invalid project ID in aggregation:', projectId);
        return;
      }

      const projectData = projectsData.get(projectId);
      if (!projectData?.isLoaded) {
        // Project not loaded yet, skip silently
        return;
      }

      // DEFENSIVE: Check if nodes array exists (list endpoint may not include nodes)
      if (!projectData.nodes || !Array.isArray(projectData.nodes)) {
        log.warn('MultiProject', `Project ${projectId} has no nodes array, skipping aggregation`);
        return;
      }

      // Add nodes with project namespacing for display purposes
      // This allows multiple projects to be shown together
      projectData.nodes.forEach((node) => {
        try {
          // Skip nodes explicitly flagged as removed/stale by backend
          const nodeRecord = node as ProjectNode & Record<string, unknown>;
          if (nodeRecord._deleted || nodeRecord.isDeleted) {
            return;
          }
          // Normalize id and parentId defensively
          const rawId: string | undefined = node.id;
          if (rawId == null) {
            log.error('MultiProject', '🚨 [AGGREGATION] Skipping node with missing id:', node);
            return;
          }
          const idStr = typeof rawId === 'string' ? rawId : String(rawId);
          const rawParentIdValue = typeof node.parentId === 'string'
            ? node.parentId
            : (typeof nodeRecord.parent_id === 'string' ? nodeRecord.parent_id : undefined);
          const rawParentId: string | undefined = rawParentIdValue;
          const parentStr = rawParentId == null ? undefined : (typeof rawParentId === 'string' ? rawParentId : String(rawParentId));

          // Check if node.id is already namespaced (contains projectId prefix)
          const isAlreadyNamespaced = typeof idStr === 'string' && idStr.startsWith(`${projectId}:`);
          const normalizedId = getNodeId(idStr);
          const normalizedParentId = parentStr ? getNodeId(parentStr) : undefined;
          const normalizedDependencies = Array.isArray(node.dependencies)
            ? node.dependencies.map((dep: string) => getNodeId(typeof dep === 'string' ? dep : String(dep ?? '')))
            : node.dependencies;

          // CRITICAL: Check for double-namespacing or wrong project namespace
          if (!isSingleProjectView && typeof idStr === 'string' && idStr.includes(':') && !idStr.startsWith(`${projectId}:`)) {
            log.error('MultiProject', '🚨 [MULTI-PROJECT] Node has wrong project namespace - FILTERING OUT!', {
              nodeId: idStr,
              expectedProject: projectId,
              actualProject: getProjectIdFromNodeId(idStr),
              fullNode: node
            });
            log.error('MultiProject', '🚨 THIS IS WHY THE NODE DISAPPEARED!');
            // Skip this node - it belongs to a different project
            return;
          }

          nodes.push({
            ...node,
            id: isSingleProjectView
              ? normalizedId
              : (isAlreadyNamespaced ? idStr : `${projectId}:${idStr}`),
            parentId: isSingleProjectView
              ? normalizedParentId
              : (parentStr ? (parentStr.startsWith(`${projectId}:`) ? parentStr : `${projectId}:${parentStr}`) : undefined),
            projectId,
            projectName: projectData.projectInfo.projectName,
            projectClient: getProjectCompanyLabel(projectData.projectInfo, projectData.client || ''),
            projectColor: projectData.color,
            originalId: normalizedId,
            // Keep dependencies within the same project only
            dependencies: isSingleProjectView
              ? normalizedDependencies
              : (Array.isArray(node.dependencies)
                ? node.dependencies.map((dep: string) => {
                    const depStr = typeof dep === 'string' ? dep : String(dep ?? '');
                    return depStr.startsWith(`${projectId}:`) ? depStr : `${projectId}:${depStr}`;
                  })
                : node.dependencies),
            // Preserve timeline fields
            startDate: node.startDate,
            endDate: node.endDate
          });
        } catch (aggErr) {
          log.error('MultiProject', '❌ [AGGREGATION] Error aggregating node; skipping', { error: aggErr, node });
          return;
        }
      });
    });

    return nodes;
  }, [visibleProjectIds, projectsData]);

  // Provide separate project data for each visible project (NOT aggregated)
  const visibleProjects = useMemo(() => {
    const visibleIds = Array.from(visibleProjectIds);
    const projects: ProjectData[] = [];

    visibleIds.forEach(projectId => {
      // Skip invalid project IDs
      if (!projectId || projectId.includes(',')) {
        // Skip invalid project IDs silently
        return;
      }

      const projectDataWithMeta = projectsData.get(projectId);
      if (!projectDataWithMeta?.isLoaded) {
        // Project not loaded yet, skip silently
        return;
      }

      // Each project maintains its own independent data structure
      projects.push({
        version: projectDataWithMeta.version || '2.0',
        projectId: projectDataWithMeta.projectId,
        projectName: projectDataWithMeta.projectInfo.projectName,
        client: projectDataWithMeta.client, // Include client field
        owner: projectDataWithMeta.owner,
        projectAssigneeId: projectDataWithMeta.projectAssigneeId ?? projectDataWithMeta.metadata?.settings?.projectAssigneeId ?? null,
        nodes: projectDataWithMeta.nodes,
        rootNodeIds: projectDataWithMeta.rootNodeIds,
        kanbanColumns: projectDataWithMeta.kanbanColumns,
        files: projectDataWithMeta.files,
        fileAuditLogs: projectDataWithMeta.fileAuditLogs,
        createdAt: projectDataWithMeta.createdAt,
        updatedAt: projectDataWithMeta.updatedAt,
        metadata: projectDataWithMeta.metadata
      });
    });

    return projects;
  }, [visibleProjectIds, projectsData]);

  // Create aggregated project data for backward compatibility
  const aggregatedData = useMemo(() => {
    // If no projects are visible, return null to clear all views
    const visibleIds = Array.from(visibleProjectIds);
    if (visibleIds.length === 0) return null;

    // Check if any visible project data is loaded
    const hasLoadedProjects = visibleIds.some(id =>
      projectsData.get(id)?.isLoaded
    );

    // If we have visible projects but none are loaded yet, return null
    if (!hasLoadedProjects) return null;

    // Choose a stable projectId for the aggregated view: prefer pinned if visible
    const pinnedProjectId = activeProjectId && visibleIds.includes(activeProjectId) ? activeProjectId : visibleIds[0] || 'default';

    // Get root node IDs (nodes without parents)
    const rootNodeIds = visibleIds.length === 1
      ? ((projectsData.get(visibleIds[0])?.rootNodeIds || []).map(id => getNodeId(id)))
      : aggregatedNodes
          .filter(node => !node.parentId)
          .map(node => node.id);

    // Use the pinned/active project as the Kanban schema source in multi-project view
    // Prefer pinned active project name in multi-project mode
    const windowGlobal = window as unknown as Record<string, unknown>;
    const multiCtx = typeof windowGlobal.__multiProjectContext === 'object' && windowGlobal.__multiProjectContext !== null
      ? windowGlobal.__multiProjectContext as { activeProjectId?: string }
      : undefined;
    const pinnedFromWindow = multiCtx?.activeProjectId ?? undefined;
    const activePinnedVisible = pinnedFromWindow && visibleIds.includes(pinnedFromWindow) ? pinnedFromWindow : undefined;
    const kanbanColumnSourceProjectId = activePinnedVisible || pinnedProjectId;
    const schemaProject = (
      projectsData.get(kanbanColumnSourceProjectId)?.isLoaded
        ? projectsData.get(kanbanColumnSourceProjectId)
        : undefined
    ) ?? visibleIds
      .map((id) => projectsData.get(id))
      .find((project) => Boolean(project?.isLoaded));
    const kanbanColumns = [...(schemaProject?.kanbanColumns || [])]
      .sort((left, right) => {
        const leftRecord = left as KanbanColumnConfig & Record<string, unknown>;
        const rightRecord = right as KanbanColumnConfig & Record<string, unknown>;
        const leftOrder = typeof leftRecord?.order === 'number' ? leftRecord.order : Number.MAX_SAFE_INTEGER;
        const rightOrder = typeof rightRecord?.order === 'number' ? rightRecord.order : Number.MAX_SAFE_INTEGER;

        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }

        return String(left?.title || left?.id || '').localeCompare(String(right?.title || right?.id || ''));
      });
    const displayName = visibleIds.length === 1
      ? (projectsData.get(visibleIds[0])?.projectInfo.projectName || 'Project')
      : (activePinnedVisible
        ? (projectsData.get(activePinnedVisible)?.projectInfo.projectName || `${visibleIds.length} Projects`)
        : `${visibleIds.length} Projects`);

    const result: ProjectData = {
      version: '2.0',
      projectId: activePinnedVisible || pinnedProjectId,
      projectName: displayName,
      client: schemaProject?.client,
      nodes: aggregatedNodes as ProjectNode[], // Use aggregated nodes from all projects
      rootNodeIds,
      kanbanColumns,
      files: schemaProject?.files || [],
      fileAuditLogs: schemaProject?.fileAuditLogs || [],
      createdAt: schemaProject?.createdAt || new Date().toISOString(),
      updatedAt: schemaProject?.updatedAt || new Date().toISOString(),
      metadata: schemaProject?.metadata || {
        settings: {
          darkMode: false,
          autoSync: true,
          syncInterval: 30,
          isCompactView: false,
          menuOrder: []
        }
      }
    };

    return result;
  }, [aggregatedNodes, visibleProjectIds, projectsData, activeProjectId]);

  return {
    aggregatedNodes,
    visibleProjects,
    aggregatedData
  };
}
